import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/helper';
import noDataIcon from '../../assets/svg/icons/no-data.svg';

/**
 * Standard UI primitives for the Shoorah admin design system.
 *
 * Single source of truth for button + card styling:
 *  - button fill : #0B1D2E (light mode) / #EAEBF0 (dark mode)  -> shoorah-btn / shoorah-btnDark
 *  - button radius: 10px                                        -> rounded-btn
 *  - card radius  : 16px                                        -> rounded-card
 *
 * The colour and radius tokens live in src/utils/tailwind-overwrites.json,
 * so changing them there updates every consumer of these components.
 */

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 text-[14px] font-medium h-[40px] md:px-5 px-3 py-1 rounded-btn transition-colors focus:outline-none';

export const BUTTON_VARIANTS = {
  primary:
    'bg-shoorah-btn text-white hover:opacity-90 dark:bg-shoorah-btnDark dark:text-shoorah-btn',
  secondary:
    'border border-shoorah-btn text-shoorah-btn bg-transparent hover:bg-shoorah-btn/5 dark:border-shoorah-btnDark dark:text-shoorah-btnDark dark:hover:bg-white/5',
  danger: 'bg-red-500 text-white hover:bg-red-600'
};

export const CARD_CLASS =
  'rounded-card border-[1.5px] border-shoorah-borderGray bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder';

/**
 * Standard form field: white background, 8px radius, black text, and a black
 * border on hover/focus. Apply to any input, textarea or select.
 */
export const FIELD_CLASS =
  'block w-full rounded-input appearance-none border border-shoorah-borderGray bg-white px-3 py-[9px] text-[14px] leading-5 text-shoorah-btn placeholder-gray-400 transition-colors hover:border-shoorah-btn focus:border-shoorah-btn focus:outline-none focus:ring-0 disabled:opacity-50 dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder dark:text-white dark:hover:border-shoorah-btnDark dark:focus:border-shoorah-btnDark';

export const FIELD_LABEL_CLASS = 'block text-sm font-medium text-shoorah-btn dark:text-white';

export const StandardInput = ({
  id,
  name,
  type = 'text',
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  isRequired = false,
  disabled = false,
  className = '',
  ...rest
}) => (
  <div>
    {label && (
      <label htmlFor={id} className={FIELD_LABEL_CLASS}>
        {label} {isRequired && !disabled && <span className="text-red-400">&#42;</span>}
      </label>
    )}
    <input
      {...rest}
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cn(
        FIELD_CLASS,
        label && 'mt-1',
        type === 'date' && 'date-field',
        error && 'border-red-400',
        className
      )}
    />
    {error && <span className="error text-xs text-red-400">{error}</span>}
  </div>
);

StandardInput.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export const StandardButton = ({
  text,
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  className = '',
  disabled = false,
  form,
  ...rest
}) => (
  <button
    {...rest}
    type={type}
    onClick={onClick}
    disabled={disabled}
    form={form}
    className={cn(
      BUTTON_BASE,
      BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
      { 'opacity-50 cursor-not-allowed': disabled },
      className
    )}
  >
    {text || children}
  </button>
);

StandardButton.propTypes = {
  text: PropTypes.string,
  children: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  form: PropTypes.string
};

/**
 * Standard dropdown: a primary-styled trigger with a chevron and a card panel
 * of single-select options separated by hairlines.
 */
export const StandardDropdown = ({
  label,
  options,
  value,
  onChange,
  align = 'left',
  variant = 'primary',
  className = '',
  buttonClassName = ''
}) => {
  const selected = options.find((option) => option.value === value);

  return (
    <Menu as="div" className={cn('relative inline-block text-left', className)}>
      <Menu.Button
        className={cn(
          BUTTON_BASE,
          BUTTON_VARIANTS[variant] || BUTTON_VARIANTS.primary,
          'whitespace-nowrap',
          buttonClassName
        )}
      >
        {selected?.label || label}
        <ChevronDownIcon className="w-4 h-4" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cn(
            'absolute z-20 mt-2 w-56 origin-top rounded-card border border-shoorah-borderGray dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor shadow-lg overflow-hidden focus:outline-none',
            align === 'right' ? 'right-0' : 'left-0'
          )}
        >
          <div className="divide-y divide-shoorah-borderGray dark:divide-shoorah-darkBorder px-4">
            {options.map((option) => (
              <Menu.Item key={option.value}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={() => onChange(option.value)}
                    className={cn(
                      'flex w-full items-center gap-2 text-left text-sm py-3 transition-colors',
                      option.value === value
                        ? 'font-medium text-shoorah-btn dark:text-shoorah-btnDark'
                        : 'text-gray-600 dark:text-gray-300',
                      active && !option.className && 'text-shoorah-primary',
                      option.className
                    )}
                  >
                    {option.icon}
                    {option.label}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

StandardDropdown.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any,
      icon: PropTypes.node,
      className: PropTypes.string
    })
  ).isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  align: PropTypes.oneOf(['left', 'right']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),
  className: PropTypes.string,
  buttonClassName: PropTypes.string
};

/**
 * Standard "no data" placeholder: centred illustration, title and optional
 * description. Use `size="sm"` inside compact cards, `size="lg"` for full pages.
 */
export const EmptyState = ({ title, description, size = 'sm', className = '' }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center border border-dashed border-gray-200 dark:border-shoorah-darkBorder rounded-card',
      size === 'lg' ? 'py-12 px-6' : 'py-8 px-4',
      className
    )}
  >
    <img
      src={noDataIcon}
      alt=""
      className={cn('mb-3 opacity-90 dark:invert', size === 'lg' ? 'w-28 h-28' : 'w-20 h-20')}
    />
    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</p>
    {description && (
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1 max-w-md">{description}</p>
    )}
  </div>
);

EmptyState.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg']),
  className: PropTypes.string
};

export const Card = ({ children, className = '', ...rest }) => (
  <div {...rest} className={cn(CARD_CLASS, className)}>
    {children}
  </div>
);

Card.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string
};

export default StandardButton;
